import { type Editor, Extension, type JSONContent } from "@tiptap/core";
import { NodeSelection } from "@tiptap/pm/state";
import { ReactRenderer } from "@tiptap/react";
import Suggestion, { type SuggestionOptions } from "@tiptap/suggestion";
import tippy from "tippy.js";

import { toast } from "@/lib/use-toast";

import { insertImagesFromPicker } from "./image-upload";
import {
  insertLayoutRow,
  layoutPresets,
  pickImageFiles,
} from "./layout-presets";
import { resolvePopupContainer } from "./popup-container";
import SlashCommandList, {
  type SlashCommandItem,
  defaultSlashCommands,
} from "./slash-command-list";

/** Commands that drop in a ready-made section, by slash-command id. */
const PRESET_COMMANDS: Record<string, () => JSONContent> = {
  section: layoutPresets.section,
  twoColumns: layoutPresets.twoColumns,
  threeColumns: layoutPresets.threeColumns,
  imageText: layoutPresets.imageText,
  divider: layoutPresets.divider,
};

/** Ask for a photo, upload it, then lay a text-over-image section on it. */
async function insertBackgroundImageSection(editor: Editor) {
  const upload = editor.storage.emailImageUpload?.upload();
  if (!upload) return;
  const [file] = await pickImageFiles();
  if (!file || editor.isDestroyed) return;
  try {
    const src = await upload(file);
    if (!editor.isDestroyed) {
      insertLayoutRow(editor, layoutPresets.backgroundImage(src));
    }
  } catch {
    toast({ title: "Failed to upload image", variant: "error" });
  }
}

export interface SlashCommandsOptions {
  suggestion: Partial<SuggestionOptions>;
  /** Called when the "AI" slash command is selected. */
  onAI?: () => void;
}

export const SlashCommands = Extension.create<SlashCommandsOptions>({
  name: "slashCommands",

  addOptions() {
    return {
      suggestion: {
        char: "/",
        startOfLine: false,
        command: ({ editor, range, props }) => {
          const item = props as SlashCommandItem;

          // Delete the slash trigger text first
          editor.chain().focus().deleteRange(range).run();

          // Execute the command action
          item.action();
        },
      },
      onAI: undefined,
    };
  },

  addProseMirrorPlugins() {
    const { onAI } = this.options;
    const editor = this.editor;

    const commandDefs = defaultSlashCommands({ onAI });

    return [
      Suggestion({
        editor,
        ...this.options.suggestion,
        allow: ({ state, range }) => {
          // Only allow slash commands at the start of a text block
          const $from = state.doc.resolve(range.from);
          const textBefore = $from.parent.textContent.slice(
            0,
            range.from - $from.start(),
          );
          // The slash should be at the beginning (possibly after whitespace)
          return textBefore.trim() === "";
        },
        items: ({ query }) => {
          const lowerQuery = query.toLowerCase();
          // Name matches first, so "/section" lands on Section rather than on
          // a heading whose description mentions the word.
          const rank = (cmd: (typeof commandDefs)[number]) =>
            cmd.label.toLowerCase().includes(lowerQuery)
              ? 0
              : cmd.keywords?.some((word) => word.includes(lowerQuery))
                ? 1
                : cmd.description.toLowerCase().includes(lowerQuery)
                  ? 2
                  : -1;
          const ranked = commandDefs
            .map((cmd) => ({ cmd, rank: rank(cmd) }))
            .filter(({ rank }) => rank >= 0)
            .sort((a, b) => a.rank - b.rank)
            .map(({ cmd }) => cmd);
          // The list draws a header per section, so keep each section's items
          // together — sections ordered by their best match.
          const sectionOrder = [...new Set(ranked.map((cmd) => cmd.section))];
          return sectionOrder
            .flatMap((section) =>
              ranked.filter((cmd) => cmd.section === section),
            )
            .map((cmd) => ({
              ...cmd,
              action: () => {
                const preset = PRESET_COMMANDS[cmd.id];
                if (preset) {
                  insertLayoutRow(editor, preset());
                  return;
                }
                switch (cmd.id) {
                  case "text":
                    editor.chain().focus().clearNodes().run();
                    break;
                  case "heading1":
                    editor
                      .chain()
                      .focus()
                      .setNode("emailHeading", { level: 1 })
                      .run();
                    break;
                  case "heading2":
                    editor
                      .chain()
                      .focus()
                      .setNode("emailHeading", { level: 2 })
                      .run();
                    break;
                  case "heading3":
                    editor
                      .chain()
                      .focus()
                      .setNode("emailHeading", { level: 3 })
                      .run();
                    break;
                  case "image":
                    void insertImagesFromPicker(editor, "slash_command");
                    break;
                  case "imageLink":
                    insertLayoutRow(editor, layoutPresets.image());
                    break;
                  case "backgroundImage":
                    void insertBackgroundImageSection(editor);
                    break;
                  case "bulletList":
                    editor.chain().focus().toggleBulletList().run();
                    break;
                  case "orderedList":
                    editor.chain().focus().toggleOrderedList().run();
                    break;
                  case "button": {
                    editor
                      .chain()
                      .focus()
                      .insertContent({
                        type: "emailButton",
                      })
                      .run();
                    // Select the row containing the button so it's protected
                    // from typing and the bubble menu appears.
                    const { $from } = editor.state.selection;
                    for (let d = $from.depth; d >= 0; d--) {
                      if ($from.node(d).type.name === "emailRow") {
                        const rowPos = $from.before(d);
                        editor.view.dispatch(
                          editor.state.tr.setSelection(
                            NodeSelection.create(editor.state.doc, rowPos),
                          ),
                        );
                        break;
                      }
                    }
                    break;
                  }
                  case "quote":
                    editor
                      .chain()
                      .focus()
                      .insertContent({
                        type: "emailBlockquote",
                      })
                      .run();
                    break;
                  case "variable":
                    editor.chain().focus().insertContent("@").run();
                    break;
                  case "ai":
                    onAI?.();
                    break;
                }
              },
            }));
        },
        render: () => {
          let component: ReactRenderer<any>;
          let popup: ReturnType<typeof tippy>[0];

          return {
            onStart: (props: any) => {
              component = new ReactRenderer(SlashCommandList, {
                props,
                editor: props.editor,
              });

              popup = tippy("body", {
                getReferenceClientRect: props.clientRect,
                appendTo: () => resolvePopupContainer(props.editor),
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: "manual",
                placement: "bottom-start",
                popperOptions: {
                  modifiers: [
                    {
                      name: "flip",
                      options: {
                        fallbackPlacements: ["top-start"],
                      },
                    },
                    {
                      name: "preventOverflow",
                      options: {
                        altAxis: true,
                        tether: false,
                      },
                    },
                  ],
                },
              })[0];
            },

            onUpdate(props: any) {
              component?.updateProps(props);
              popup?.setProps({
                getReferenceClientRect: props.clientRect,
              });
            },

            onKeyDown(props: any) {
              if (props.event.key === "Escape") {
                popup?.hide();
                return true;
              }
              return component?.ref?.onKeyDown(props) ?? false;
            },

            onExit() {
              popup?.destroy();
              component?.destroy();
            },
          };
        },
      }),
    ];
  },
});

/**
 * Create a configured SlashCommands extension instance.
 */
export function createSlashCommands(options?: { onAI?: () => void }) {
  return SlashCommands.configure({
    onAI: options?.onAI,
  });
}
